import { routes } from "@constants/routes";
import {
  Actions,
  ArrowRightIcon,
  DeleteIcon,
  FormGridContainer,
  handleApiRequest,
  PlusIcon,
  SearcherInput,
  Typography,
} from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationJobPositionStatusChoices,
  GetJobPositionByIdQuery,
  UpdateJobPositionStatusMutation,
  UpdateJobPositionStatusMutationVariables,
  useGetCitiesLazyQuery,
  useGetCountriesLazyQuery,
  useGetRegionsLazyQuery,
  useUpdateJobPositionStatusMutation,
} from "@graphql/generated";
import { Button } from "@heroui/react";
import { theme } from "@theme/index";
import { useEffect } from "react";
import { FieldValues, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useCreateJobPosition } from "../useCreateJobPosition";
import { useUpdateJobPosition } from "../useUpdateJobPosition";
import { inputConfig } from "./formConfig";

interface IJobDetailsStepContentProps {
  handleNextStep: () => void;
  jobPositionData?: NonNullable<NonNullable<GetJobPositionByIdQuery["organization"]>["jobPosition"]>["get"];
  isLoading?: boolean;
}

interface Location {
  country: string;
  region: string;
  city: string;
}

const JobDetailsStepContent = ({ handleNextStep, jobPositionData, isLoading }: IJobDetailsStepContentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const formHandler = useForm<FieldValues>({
    defaultValues: {
      locations: [{ country: "", region: "", city: "" }],
    },
  });

  formHandler.watch("validityDays");

  const { fields, append, remove } = useFieldArray({
    control: formHandler.control,
    name: "locations",
  });
  const [searchParams] = useSearchParams();
  const { handleSubmit: handleSubmitCreate, loading: submitLoading } = useCreateJobPosition();
  const { handleSubmit: handleSubmitUpdate, loading: updateLoading } = useUpdateJobPosition();
  const [updateJobPositionStatus, { loading: updateStatusLoading }] = useUpdateJobPositionStatusMutation();

  const isEdit = location.pathname.includes("edit");

  const transformFormData = (data: any) => {
    const getHiringInCanada = (value: string) => {
      switch (value) {
        case "true":
          return true;
        case "false":
          return false;
        default:
          return null;
      }
    };
    return {
      ...data,
      hiringInCanada: getHiringInCanada(
        typeof data?.hiringInCanada === "string" ? data?.hiringInCanada : data?.hiringInCanada?.value,
      ),
    };
  };

  const handleClickSubmit = formHandler.handleSubmit(async (data: FieldValues) => {
    try {
      const transformedData = transformFormData(data);
      const cities = transformedData?.locations?.map((location: Location) => location?.city).filter(Boolean) || [];
      if (cities.length === 0) {
        toast.error("Please select at least one location");
        return;
      }

      delete transformedData.locations;
      transformedData.cities = cities;
      transformedData.validityDays = transformedData?.validityDays?.value
        ? parseInt(transformedData?.validityDays?.value)
        : undefined;

      if (jobPositionData) {
        const id = searchParams.get("id");

        if (!id) return;

        if (isEdit && jobPositionData?.status !== AuthAccountOrganizationJobPositionStatusChoices.DRAFTED) {
          await handleApiRequest<UpdateJobPositionStatusMutation, UpdateJobPositionStatusMutationVariables>({
            apiFunction: updateJobPositionStatus,
            variables: {
              id,
              status: AuthAccountOrganizationJobPositionStatusChoices.SUBMITTED,
            },
            componentName: "JobDetailsStepContent",
            actionName: "updateJobPositionStatus",
            displaySuccessToast: false,
            fallbackErrorMessage: "Failed to update job post. Please try again.",
            onError: () => {
              navigate(routes.jobPositions.main);
            },
            extraErrorContext: {
              jobPositionId: id,
            },
          });
        }

        await handleSubmitUpdate({
          id,
          handleNextStep,
          values: transformedData,
        });
      } else {
        await handleSubmitCreate({
          handleNextStep,
          values: transformedData,
        });
      }
    } catch (error) {
      console.error(error);
    }
  });

  useEffect(() => {
    if (jobPositionData && !isLoading) {
      formHandler.reset({
        title: jobPositionData?.title,
        vaccancy: jobPositionData?.vaccancy,
        validityDays: jobPositionData?.validityDays ? jobPositionData?.validityDays.toString() : undefined,
        startAt: jobPositionData?.startAt,
        locationType: jobPositionData?.locationType,
        description: jobPositionData?.description,
        hiringInCanada: jobPositionData?.hiringInCanada !== null ? jobPositionData?.hiringInCanada?.toString() : "null",
        locations:
          jobPositionData?.cities?.edges?.map((location) => ({
            country: { id: location?.node?.country?.id, name: location?.node?.country?.name },
            region: { id: location?.node?.region?.id, name: location?.node?.region?.name },
            city: location?.node,
          })) || [],
      });
    }
  }, [formHandler, jobPositionData, isLoading]);

  const addLocation = () => {
    append({ country: "", region: "", city: "" });
  };

  return (
    <div className="mb-8">
      <Typography className="font-bold">{isEdit ? "Edit" : "New"} Job Details:</Typography>
      <Typography className="text-xs mb-5 mt-3">
        In this section, define the basic information about the new job post, including the job title, description,
        number of vacancies, initial requirements, and the start date
      </Typography>

      <FormGridContainer formHandler={formHandler} inputConfigs={inputConfig()} />

      <div className="mt-4 flex flex-col gap-2">
        <Typography className="font-semibold mb-2">Locations:</Typography>
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="flex lg:flex-row flex-col gap-4 mb-4 items-end border-1 p-4 rounded-md shadow-sm border-gray-300 bg-zinc-50 pb-4"
          >
            <div className="w-full lg:w-1/3">
              <SearcherInput
                label="Country:"
                dataPath="common.countries"
                useLazyQuery={useGetCountriesLazyQuery}
                onChange={(value) => {
                  if (Array.isArray(value) || typeof value === "string") return;
                  formHandler.setValue(`locations.${index}.country`, value?.node);
                  formHandler.setValue(`locations.${index}.region`, undefined);
                  formHandler.setValue(`locations.${index}.city`, undefined);
                }}
                value={formHandler.watch(`locations.${index}.country`)}
              />
            </div>
            <div className="w-full lg:w-1/3">
              <SearcherInput
                label="Province:"
                isDisabled={!formHandler.watch(`locations.${index}.country`)}
                useLazyQuery={useGetRegionsLazyQuery}
                dataPath="common.regions"
                refetchDataKey={formHandler.watch(`locations.${index}.country`)?.id}
                additionalFilters={{
                  country: formHandler.watch(`locations.${index}.country`)?.id,
                }}
                onChange={(value) => {
                  if (Array.isArray(value) || typeof value === "string") return;
                  formHandler.setValue(`locations.${index}.region`, value?.node);
                  formHandler.setValue(`locations.${index}.city`, undefined);
                }}
                value={formHandler.watch(`locations.${index}.region`)}
              />
            </div>
            <div className="w-full lg:w-1/3">
              <SearcherInput
                label="City"
                placeholder="Select City"
                isDisabled={!formHandler.watch(`locations.${index}.region`)}
                useLazyQuery={useGetCitiesLazyQuery}
                dataPath="common.cities"
                refetchDataKey={formHandler.watch(`locations.${index}.region`)?.id}
                additionalFilters={{
                  region: formHandler.watch(`locations.${index}.region`)?.id,
                }}
                onChange={(value) => {
                  if (Array.isArray(value) || typeof value === "string") return;
                  formHandler.setValue(`locations.${index}.city`, value?.node);
                }}
                value={formHandler.watch(`locations.${index}.city`)}
              />
            </div>
            <Button
              type="button"
              variant="bordered"
              className="w-full lg:w-fit min-w-0 py-2 px-2 h-fit rounded-lg border-2 border-primary-500"
              onPress={() => remove(index)}
            >
              <DeleteIcon
                width={20}
                height={20}
                viewBox="0 0 20 20"
                stroke={theme.themes?.light?.colors?.primary?.[500]}
              />
            </Button>
          </div>
        ))}
        <Button
          onPress={addLocation}
          variant="bordered"
          startContent={<PlusIcon width={20} height={20} viewBox="0 0 20 20" className="stroke-green-600" />}
          className="w-full lg:w-fit min-w-0 py-2 px-2 h-fit rounded-lg border-2 border-green-600 text-green-600 font-medium self-end"
        >
          Add New Location
        </Button>
      </div>

      <Actions
        className="mt-6"
        showCancel={false}
        submitProps={{
          onClick: handleClickSubmit,
          isLoading: submitLoading || updateLoading || updateStatusLoading,
          isDisabled: submitLoading || updateLoading || updateStatusLoading,
          children: "Next",
          endContent: <ArrowRightIcon stroke="#fff" className="w-4 h-4" />,
        }}
      />
    </div>
  );
};

export default JobDetailsStepContent;
